import { DOCUMENT, Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { NavigationService, PREVENT_ROUTE_REFRESH } from '@phx/core';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { authActions } from './auth.actions';
import { authFeature } from './auth.state';

const MERCHANT_UID_QUERYPARAM_KEY = 'merchantUid';

@Injectable()
export class AuthEffects {
    constructor(
        private actions$: Actions,
        private authService: AuthService,
        private router: Router,
        private navigationService: NavigationService,
        private store: Store,
        @Inject(DOCUMENT) private document: Document
    ) {}

    login$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.login),
            exhaustMap(action =>
                this.authService.login(action.credentials).pipe(
                    map(user => {
                        return authActions.loginSucceeded({ user, impersonateAs: action.impersonateAs });
                    }),
                    catchError(error => {
                        if (error.code === 401) {
                            this.router.navigate(['/']);
                        }

                        return of(authActions.loginFailed(error));
                    })
                )
            )
        )
    );

    loginSucceded$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.loginSucceeded),
                concatLatestFrom(() => this.store.select(authFeature.selectReturnUrl)),
                tap(([_, returnUrl]) => {
                    const refreshForbidden = !!this.navigationService.routerStateSnapshot.data?.[PREVENT_ROUTE_REFRESH];

                    if (returnUrl) {
                        this.router.navigateByUrl(returnUrl, { replaceUrl: true });
                    } else if (!refreshForbidden) {
                        this.navigationService.refreshCurrentRoute();
                    }
                    // TODO: call a cleanup login action?
                })
            ),
        { dispatch: false }
    );

    logout$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.logout),
            exhaustMap(action =>
                this.authService.logout().pipe(
                    map(() => authActions.logoutSucceeded({ targetPath: action.targetPath })),
                    catchError(error => of(authActions.logoutFailed({ error })))
                )
            )
        )
    );

    logoutHappened$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(authActions.logoutSucceeded, authActions.logoutFailed),
                tap(action => {
                    // force reload
                    if (action.type === authActions.logoutSucceeded.type) {
                        this.document.location.href = action.targetPath || '/';
                    } else {
                        this.document.location.href = '/';
                    }
                })
            ),
        { dispatch: false }
    );

    autoLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.autologin),
            concatLatestFrom(() => this.store.select(authFeature.selectAuthState)),
            exhaustMap(([autoLogin, authState]) => {
                if (!authState.loaded) {
                    return this.authService.autoLogin().pipe(
                        map(apiResponse =>
                            authActions.autologinSucceeded({
                                user: apiResponse.data,
                                isSemiAuth:
                                    apiResponse.headers?.get('x-phx-authentication-state') === 'SEMI_AUTHENTICATED'
                            })
                        ),
                        catchError(error => of(authActions.autologinFailed(error)))
                    );
                } else {
                    return of(authActions.autologinCancelled());
                }
            })
        )
    );

    impersonateAfterLogin$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.autologinSucceeded, authActions.loginSucceeded),
            concatLatestFrom(() => this.store.select(authFeature.selectIsImpersonatingAllowed)),
            exhaustMap(([action, isImpersonatingAllowed]) => {
                let uidToImpersonate: string = null;

                if (action.type === authActions.loginSucceeded.type) {
                    // For login form/page within Cockpit
                    uidToImpersonate = action.impersonateAs;
                } else {
                    // For merchant-portal redirects (normal customer/merchant behaviour)
                    uidToImpersonate = new URL(this.document.location.toString()).searchParams.get(
                        MERCHANT_UID_QUERYPARAM_KEY
                    );
                }

                if (!uidToImpersonate || !isImpersonatingAllowed) {
                    return of(authActions.skipImpersonation());
                }

                return of(authActions.impersonate({ impersonatedUserId: uidToImpersonate }));
            })
        )
    );

    impersonate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(authActions.impersonate),
            exhaustMap(action =>
                this.authService.impersonate(action.impersonatedUserId).pipe(
                    map(user => {
                        return authActions.impersonateSucceeded({ user });
                    }),
                    catchError(error => of(authActions.impersonateFailed({ error })))
                )
            )
        )
    );
}
